import { IconAlertsAlarm } from '@doltech/icons/IconAlertsAlarm';
import { IconDuotoneSolidColorsLightPlay2 } from '@doltech/icons/IconDuotoneSolidColorsLightPlay2';
import { IconOtherQuestionnaire1 } from '@doltech/icons/IconOtherQuestionnaire1';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import WarningModal from '@doltech/ui/lib/figma/Modal/WarningModal';
import WarningModalV2 from '@doltech/ui/lib/figma/Modal/WarningModalV2';
import { ReadingText } from '@doltech/ui/lib/figma/Typography';
import { Modal } from 'antd';
import { observer } from 'mobx-react-lite';
import { nanoid } from 'nanoid';
import * as React from 'react';
import Timer from 'react-compound-timer';
import styled from 'styled-components';

import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { ModalType, ReviewStatus } from '../model/test-submissions.store';

const TIME_COUNTDOWN = 15000;
const DELAY_TIME_COUNTDOWN = 1000;
const COUNTDOWN_UPDATE_INTERVAL = 500;

interface TimeUpModalProps {
  onTimeout?: () => void;
}

const Main = styled.div`
  margin-top: 22px;
  .desc,
  .time-countdown {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: ${colorsV2.black100};
  }
  .time-countdown {
    color: ${colorsV2.red100};
  }
`;

const IconWrapper = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: ${colorsV2.gray10};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 16px;
`;

export const TimeUpModal = observer((props: TimeUpModalProps) => {
  const { onTimeout } = props;

  const [isStartCountdown, setIsStartCountdown] = React.useState(false);
  const testSubmissionStore = useTestSubmissionStore();

  React.useEffect(() => {
    if (testSubmissionStore.timeUpModalVisible === true) {
      // add a delay before start countdown - better ux
      setTimeout(() => {
        setIsStartCountdown(true);
      }, DELAY_TIME_COUNTDOWN);
    }
  }, [testSubmissionStore.timeUpModalVisible]);

  if (testSubmissionStore.timeUpModalVisible === false) {
    return null;
  }
  Modal.destroyAll();
  return (
    <WarningModal
      isModalOpen={testSubmissionStore.timeUpModalVisible}
      closable={false}
      icon={
        <IconWrapper>
          <IconDuotoneSolidColorsLightPlay2 />
        </IconWrapper>
      }
      isShowFooter={false}
      title={"Time's up"}
    >
      <Main>
        <ReadingText className="desc">We will redirect you to home page </ReadingText>
        <Timer
          key={`${nanoid()}${testSubmissionStore.timeUpModalVisible}${isStartCountdown}`}
          initialTime={TIME_COUNTDOWN}
          direction="backward"
          timeToUpdate={COUNTDOWN_UPDATE_INTERVAL}
          startImmediately={isStartCountdown}
          checkpoints={[
            {
              time: 0,
              callback: () => {
                testSubmissionStore.closeTimeUpModal();
                onTimeout();
              },
            },
          ]}
        >
          {renderTimer()}
        </Timer>
      </Main>
    </WarningModal>
  );
});

export const SectionTimeUpModal = observer(() => {
  const [isStartCountdown, setIsStartCountdown] = React.useState(false);
  const testSubmissionStore = useTestSubmissionStore();

  React.useEffect(() => {
    if (testSubmissionStore.sectionTimeUpModalVisible === true) {
      // add a delay before start countdown - better ux
      setTimeout(() => {
        setIsStartCountdown(true);
      }, DELAY_TIME_COUNTDOWN);
    }
  }, [testSubmissionStore.sectionTimeUpModalVisible]);

  if (testSubmissionStore.sectionTimeUpModalVisible === false) {
    return null;
  }
  return (
    <WarningModalV2
      isModalOpen={testSubmissionStore.sectionTimeUpModalVisible}
      closable={false}
      icon={<IconAlertsAlarm fontSize={36} color={colorsV2.yellow100} />}
      title={"Time's up"}
      okButtonText="OK"
      onOk={() => {
        testSubmissionStore.closeSectionTimeUpModal();
        testSubmissionStore.nextSection();
      }}
    >
      <Main>
        <ReadingText className="desc">
          Đã hết giờ rồi, bạn phải chuyển qua section {testSubmissionStore.currentSectionIndex + 2}{' '}
          rồi,
        </ReadingText>
        <Timer
          key={`${nanoid()}${testSubmissionStore.timeUpModalVisible}${isStartCountdown}`}
          initialTime={TIME_COUNTDOWN}
          direction="backward"
          timeToUpdate={COUNTDOWN_UPDATE_INTERVAL}
          startImmediately={isStartCountdown}
          checkpoints={[
            {
              time: 0,
              callback: () => {
                testSubmissionStore.closeSectionTimeUpModal();
                testSubmissionStore.nextSection();
              },
            },
          ]}
        >
          {({ getTime }) => {
            return (
              <ReadingText className="desc">
                <ReadingText className="time-countdown">
                  {Math.floor(getTime() / 1000)}s đếm ngược
                </ReadingText>
                . Đừng lo, tới phần review mình sẽ được xem và sửa lại những câu chưa chắc.
              </ReadingText>
            );
          }}
        </Timer>
      </Main>
    </WarningModalV2>
  );
});

interface ReviewTimeUpModalProps {
  onTimeout: () => void;
}

export const ReviewTimeUpModal = observer((props: ReviewTimeUpModalProps) => {
  const { onTimeout } = props;
  const [isStartCountdown, setIsStartCountdown] = React.useState(false);
  const testSubmissionStore = useTestSubmissionStore();

  React.useEffect(() => {
    if (testSubmissionStore.reviewTimeUpModalVisible === true) {
      // add a delay before start countdown - better ux
      setTimeout(() => {
        setIsStartCountdown(true);
      }, DELAY_TIME_COUNTDOWN);
    }
  }, [testSubmissionStore.reviewTimeUpModalVisible]);

  if (testSubmissionStore.reviewTimeUpModalVisible === false) {
    return null;
  }
  return (
    <WarningModalV2
      isModalOpen={testSubmissionStore.reviewTimeUpModalVisible}
      closable={false}
      icon={<IconAlertsAlarm fontSize={36} color={colorsV2.yellow100} />}
      title={"Time's up"}
      okButtonText="Chuyển ngay"
      onOk={onTimeout}
    >
      <Main>
        <Timer
          key={`${nanoid()}${testSubmissionStore.reviewTimeUpModalVisible}${isStartCountdown}`}
          initialTime={TIME_COUNTDOWN}
          direction="backward"
          timeToUpdate={COUNTDOWN_UPDATE_INTERVAL}
          startImmediately={isStartCountdown}
          checkpoints={[
            {
              time: 0,
              callback: () => {
                testSubmissionStore.closeReviewTimeUpModal();
                onTimeout();
              },
            },
          ]}
        >
          {({ getTime }) => {
            return (
              <ReadingText className="desc">
                Đã hết giờ rồi, bạn sẽ được chuyển qua trang xem đáp án{' '}
                <ReadingText className="time-countdown">
                  trong {Math.floor(getTime() / 1000)} giây
                </ReadingText>{' '}
                nữa
              </ReadingText>
            );
          }}
        </Timer>
      </Main>
    </WarningModalV2>
  );
});

export const GoToTimeReviewModal = observer((props: TimeUpModalProps) => {
  const [isStartCountdown, setIsStartCountdown] = React.useState(false);
  const testSubmissionStore = useTestSubmissionStore();
  const { totalQuestions, answeredCount, reviewTime, modal } = testSubmissionStore;

  const isShow = modal === ModalType.GO_TO_REVIEW;

  React.useEffect(() => {
    if (isShow) {
      // add a delay before start countdown - better ux
      setTimeout(() => {
        setIsStartCountdown(true);
      }, DELAY_TIME_COUNTDOWN);
    }
  }, [isShow]);

  if (!isShow) {
    return null;
  }

  const renderMessage = () => {
    const reviewTimeInMinutes = Math.floor(reviewTime / 60000);
    if (answeredCount === totalQuestions) {
      return `Bạn có ${Math.floor(
        reviewTimeInMinutes
      )} phút để kiểm tra lại các câu chưa chắc nha.`;
    }
    return `Bạn có ${Math.floor(reviewTimeInMinutes)} phút điền ${
      totalQuestions - answeredCount
    } câu còn thiếu.`;
  };

  return (
    <WarningModalV2
      isModalOpen={isShow}
      closable={false}
      icon={<IconOtherQuestionnaire1 fontSize={24} color={colorsV2.blue100} />}
      title="Review time"
      okButtonText="OK"
      onOk={() => {
        testSubmissionStore.hideModal();
        testSubmissionStore.setReviewStatus(ReviewStatus.REVIEWING);
      }}
    >
      <Main>
        <ReadingText className="desc">{renderMessage()} </ReadingText>
        <Timer
          key={`${nanoid()}`}
          initialTime={TIME_COUNTDOWN}
          direction="backward"
          timeToUpdate={COUNTDOWN_UPDATE_INTERVAL}
          startImmediately={isStartCountdown}
          checkpoints={[
            {
              time: 0,
              callback: () => {
                testSubmissionStore.hideModal();
                testSubmissionStore.setReviewStatus(ReviewStatus.REVIEWING);
              },
            },
          ]}
        />
      </Main>
    </WarningModalV2>
  );
});

const renderTimer = () => (
  <ReadingText className="time-countdown">
    in <Timer.Seconds /> seconds
  </ReadingText>
);
